import type { Pos, FuelGrid, Direction } from "../types.ts";
import { cloneGrid, getFuel, setFuel, inBounds, getNeighbor, getNeighbors, totalFuel } from "./grid.ts";

export interface MoveResult {
  grid: FuelGrid;
  playerPos: Pos;
  won: boolean;
  stuck: boolean;
}

export function canStartAt(grid: FuelGrid, pos: Pos): boolean {
  return getFuel(grid, pos) > 0;
}

export function canMoveTo(grid: FuelGrid, playerPos: Pos, target: Pos): boolean {
  if (!inBounds(grid, target)) return false;
  if (getFuel(grid, target) <= 0) return false;
  const dr = Math.abs(target.row - playerPos.row);
  const dc = Math.abs(target.col - playerPos.col);
  return (dr + dc) === 1;
}

export function getValidMoves(grid: FuelGrid, playerPos: Pos): Pos[] {
  return getNeighbors(grid, playerPos).filter((n) => getFuel(grid, n) > 0);
}

export function applyStart(grid: FuelGrid, pos: Pos): MoveResult {
  const newGrid = cloneGrid(grid);
  setFuel(newGrid, pos, getFuel(newGrid, pos) - 1);
  const won = totalFuel(newGrid) === 0;
  const stuck = !won && getValidMoves(newGrid, pos).length === 0;
  return { grid: newGrid, playerPos: pos, won, stuck };
}

export function applyMove(grid: FuelGrid, playerPos: Pos, target: Pos): MoveResult {
  const newGrid = cloneGrid(grid);
  setFuel(newGrid, target, getFuel(newGrid, target) - 1);
  const won = totalFuel(newGrid) === 0;
  const stuck = !won && getValidMoves(newGrid, target).length === 0;
  return { grid: newGrid, playerPos: target, won, stuck };
}

export function applyDirectionMove(
  grid: FuelGrid,
  playerPos: Pos,
  dir: Direction
): MoveResult | null {
  const target = getNeighbor(playerPos, dir);
  if (!inBounds(grid, target) || getFuel(grid, target) <= 0) return null;
  return applyMove(grid, playerPos, target);
}

export function isStuck(grid: FuelGrid, playerPos: Pos): boolean {
  return totalFuel(grid) > 0 && getValidMoves(grid, playerPos).length === 0;
}

export function checkWin(grid: FuelGrid): boolean {
  return totalFuel(grid) === 0;
}
